import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  ActivityMeasurement,
  Program,
  ProgramSessionReference,
  ProgramWeek,
  Session,
} from "../types/post";
import { sessionService } from "./sessionService";
import { getUserPublicData } from "./userService";
import {
  computeProgramProgress,
  ProgramAssignmentLike,
  ProgramProgress,
  resolveProgramStartDate,
} from "../utils/programProgress";

const PROGRAM_COLLECTION = "programs";
const PROGRAMS_SUBCOLLECTION = "programs";
const ORGANISATIONS_COLLECTION = "organisations";
const MAX_PROGRAM_WEEKS = 12;

/**
 * A coach program as written by PeakElite at
 * `organisations/{orgId}/programs/{programId}` (the athlete's per-athlete
 * copy). `startDate` and `assignments` are optional on real docs; callers go
 * through `resolveProgramStartDate` instead of trusting either.
 */
export interface OrganisationProgram {
  id: string;
  organisationId: string;
  name: string;
  description: string;
  startDate?: string;
  durationWeeks: number;
  weeks: ProgramWeek[];
  linkedSessions: ProgramSessionReference[];
  assignments: ProgramAssignmentLike[];
  createdBy?: string;
  masterProgramId?: string;
  isActive?: boolean;
}

export type ProgramAssigner =
  | { kind: "self" }
  | {
      kind: "coach";
      coachId?: string;
      coachName?: string;
      coachImageUrl?: string;
      organisationName?: string;
    };

/**
 * A program as the athlete sees it (plan 3g): the doc (or a synthetic
 * stand-in built from `programLinkage` when the doc is gone or unreadable),
 * its sessions, who assigned it, and the progress model.
 */
export interface AthleteProgram {
  id: string;
  source: "self" | "organisation";
  organisationId?: string;
  name: string;
  description: string;
  /** Resolved and normalised to a Monday. */
  startDate: string;
  durationWeeks: number;
  weeks: ProgramWeek[];
  /** Only meaningful for self programs (drives the Schedule filter). */
  isActive?: boolean;
  /** True when the program doc could not be read and linkage data was used. */
  fromLinkageOnly: boolean;
  assigner: ProgramAssigner;
  sessions: (Session & { id: string })[];
  progress: ProgramProgress;
}

/** Route for the read-only detail view of an athlete program. */
export const programDetailPath = (
  program: Pick<AthleteProgram, "id" | "organisationId">,
): string =>
  program.organisationId
    ? `/programs/${program.id}/detail?org=${encodeURIComponent(program.organisationId)}`
    : `/programs/${program.id}/detail`;

export const formatAssigner = (assigner: ProgramAssigner): string => {
  if (assigner.kind === "self") return "You";
  const parts = [assigner.coachName, assigner.organisationName].filter(
    (p): p is string => Boolean(p && p.trim()),
  );
  return parts.length > 0 ? parts.join(" · ") : "Your coach";
};

const organisationNameCache = new Map<string, Promise<string | null>>();

const clampDurationWeeks = (durationWeeks: number): number => {
  const parsed = Number(durationWeeks);
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(MAX_PROGRAM_WEEKS, Math.max(1, Math.trunc(parsed)));
};

export const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseLocalDateString = (value?: string): Date | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime()) || getLocalDateString(parsed) !== value) {
    return null;
  }

  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const getStartOfWeek = (date: Date): Date => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  return start;
};

export const normalizeProgramStartDate = (
  startDate?: string,
  linkedSessions: ProgramSessionReference[] = [],
): string => {
  const linkedDates = linkedSessions
    .map((sessionRef) => parseLocalDateString(sessionRef?.plannedDate))
    .filter((value): value is Date => Boolean(value))
    .sort((left, right) => left.getTime() - right.getTime());

  const baseDate =
    parseLocalDateString(startDate) ||
    linkedDates[0] ||
    getStartOfWeek(new Date());

  return getLocalDateString(getStartOfWeek(baseDate));
};

const toJsDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  return undefined;
};

const sanitizeForFirestore = (value: any): any => {
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeForFirestore(item))
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === "object") {
    if (value instanceof Date || typeof value?.toDate === "function") {
      return value;
    }

    return Object.entries(value).reduce<Record<string, any>>(
      (acc, [key, item]) => {
        const sanitized = sanitizeForFirestore(item);
        if (sanitized !== undefined) {
          acc[key] = sanitized;
        }
        return acc;
      },
      {},
    );
  }

  return value === undefined ? undefined : value;
};

export const buildProgramWeeks = (
  durationWeeks: number,
  existingWeeks: ProgramWeek[] = [],
): ProgramWeek[] => {
  const safeDuration = clampDurationWeeks(durationWeeks);
  const existingByWeek = new Map(
    existingWeeks.map((week) => [week.weekNumber, week]),
  );

  return Array.from({ length: safeDuration }, (_, index) => {
    const weekNumber = index + 1;
    const existingWeek = existingByWeek.get(weekNumber);

    return existingWeek?.periodisation
      ? { weekNumber, periodisation: existingWeek.periodisation }
      : { weekNumber };
  });
};

const normalizeLinkedSessions = (
  linkedSessions: ProgramSessionReference[] = [],
): ProgramSessionReference[] => {
  const seen = new Set<string>();

  return linkedSessions.reduce<ProgramSessionReference[]>((acc, sessionRef) => {
    if (!sessionRef?.sessionId || !sessionRef?.plannedDate) {
      return acc;
    }

    if (seen.has(sessionRef.sessionId)) {
      return acc;
    }

    seen.add(sessionRef.sessionId);
    acc.push(
      sanitizeForFirestore({
        sessionId: sessionRef.sessionId,
        plannedDate: sessionRef.plannedDate,
        weekNumber: sessionRef.weekNumber,
        dayNumber: sessionRef.dayNumber,
      }),
    );
    return acc;
  }, []);
};

const toProgram = (id: string, data: any): Program & { id: string } => {
  const durationWeeks = clampDurationWeeks(data?.durationWeeks ?? 1);
  const linkedSessions = normalizeLinkedSessions(
    Array.isArray(data?.linkedSessions) ? data.linkedSessions : [],
  );

  return {
    id,
    name: typeof data?.name === "string" ? data.name : "",
    description: typeof data?.description === "string" ? data.description : "",
    startDate: normalizeProgramStartDate(data?.startDate, linkedSessions),
    durationWeeks,
    weeks: buildProgramWeeks(
      durationWeeks,
      Array.isArray(data?.weeks) ? data.weeks : [],
    ),
    linkedSessions,
    isActive: typeof data?.isActive === "boolean" ? data.isActive : false,
    createdAt: toJsDate(data?.createdAt),
    updatedAt: toJsDate(data?.updatedAt),
  };
};

const getProgramCollection = (userId: string) =>
  collection(db, PROGRAM_COLLECTION, userId, PROGRAMS_SUBCOLLECTION);

const getProgramDoc = (userId: string, programId: string) =>
  doc(db, PROGRAM_COLLECTION, userId, PROGRAMS_SUBCOLLECTION, programId);

type ProgramInput = Omit<Program, "id" | "createdAt" | "updatedAt">;
type ProgramLinkedSessionInput = {
  ActivityMeasurementList?: ActivityMeasurement[];
  plannedDate: string;
  name?: string;
  description: string;
  trainingTypeID?: string;
  /** @deprecated Legacy composite ID kept for old callers; use trainingTypeID */
  sessionTypeID?: string;
  periodisation?: ProgramWeek["periodisation"];
  status?: "planned" | "executing" | "complete";
  startTime?: string;
  endTime?: string;
  linkedTrainingLogID?: string;
};

export class ProgramService {
  static async createProgram(
    userId: string,
    programData: ProgramInput,
  ): Promise<string> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    const name = programData.name?.trim();
    if (!name) {
      throw new Error("Program name is required");
    }

    const durationWeeks = clampDurationWeeks(programData.durationWeeks);
    const firestoreData = sanitizeForFirestore({
      name,
      description: programData.description?.trim() || "",
      startDate: normalizeProgramStartDate(
        programData.startDate,
        normalizeLinkedSessions(programData.linkedSessions),
      ),
      durationWeeks,
      weeks: buildProgramWeeks(durationWeeks, programData.weeks),
      linkedSessions: normalizeLinkedSessions(programData.linkedSessions),
      isActive: programData.isActive ?? false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    const docRef = await addDoc(getProgramCollection(userId), firestoreData);
    return docRef.id;
  }

  static async getProgram(
    userId: string,
    programId: string,
  ): Promise<(Program & { id: string }) | null> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!programId) {
      throw new Error("Program ID is required");
    }

    const docSnap = await getDoc(getProgramDoc(userId, programId));
    if (!docSnap.exists()) {
      return null;
    }

    return toProgram(docSnap.id, docSnap.data());
  }

  static async getAllPrograms(
    userId: string,
  ): Promise<(Program & { id: string })[]> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    const q = query(getProgramCollection(userId), orderBy("updatedAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((docSnap) =>
      toProgram(docSnap.id, docSnap.data()),
    );
  }

  static async updateProgram(
    userId: string,
    programId: string,
    updates: Partial<ProgramInput>,
  ): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!programId) {
      throw new Error("Program ID is required");
    }

    const currentProgram = await this.getProgram(userId, programId);
    if (!currentProgram) {
      throw new Error("Program not found");
    }

    const nextDurationWeeks = clampDurationWeeks(
      updates.durationWeeks ?? currentProgram.durationWeeks,
    );
    const nextWeeks = buildProgramWeeks(
      nextDurationWeeks,
      updates.weeks ?? currentProgram.weeks,
    );
    const nextLinkedSessions = normalizeLinkedSessions(
      updates.linkedSessions ?? currentProgram.linkedSessions,
    );
    const nextStartDate = normalizeProgramStartDate(
      updates.startDate ?? currentProgram.startDate,
      nextLinkedSessions,
    );

    const cleanedUpdates = sanitizeForFirestore({
      ...(updates.name !== undefined ? { name: updates.name.trim() } : {}),
      ...(updates.description !== undefined
        ? { description: updates.description.trim() }
        : {}),
      startDate: nextStartDate,
      durationWeeks: nextDurationWeeks,
      weeks: nextWeeks,
      linkedSessions: nextLinkedSessions,
      ...(updates.isActive !== undefined ? { isActive: updates.isActive } : {}),
      updatedAt: Timestamp.now(),
    });

    if (cleanedUpdates.name === "") {
      throw new Error("Program name is required");
    }

    await updateDoc(getProgramDoc(userId, programId), cleanedUpdates);
  }

  static async createProgramLinkedSession(
    userId: string,
    programId: string,
    sessionData: ProgramLinkedSessionInput,
    placement: Pick<ProgramSessionReference, "weekNumber" | "dayNumber"> = {},
  ): Promise<string> {
    const program = await this.getProgram(userId, programId);
    if (!program) {
      throw new Error("Program not found");
    }

    const sessionId = await sessionService.createSession(userId, {
      ActivityMeasurementList: sessionData.ActivityMeasurementList || [],
      plannedDate: sessionData.plannedDate,
      name: sessionData.name,
      description: sessionData.description || "",
      trainingTypeID: sessionData.trainingTypeID,
      sessionTypeID: sessionData.sessionTypeID,
      periodisation: sessionData.periodisation,
      status: sessionData.status,
      startTime: sessionData.startTime,
      endTime: sessionData.endTime,
      linkedTrainingLogID: sessionData.linkedTrainingLogID,
      programLinkage: sanitizeForFirestore({
        programId: program.id,
        programName: program.name,
        durationWeeks: program.durationWeeks,
        weekNumber: placement.weekNumber,
        dayNumber: placement.dayNumber,
      }),
    });

    await this.updateProgram(userId, programId, {
      linkedSessions: [
        ...program.linkedSessions,
        {
          sessionId,
          plannedDate: sessionData.plannedDate,
          weekNumber: placement.weekNumber,
          dayNumber: placement.dayNumber,
        },
      ],
    });

    return sessionId;
  }

  static async deleteProgram(userId: string, programId: string): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!programId) {
      throw new Error("Program ID is required");
    }

    const program = await this.getProgram(userId, programId);
    const sessionIds = Array.from(
      new Set(
        (program?.linkedSessions || []).map(
          (sessionRef) => sessionRef.sessionId,
        ),
      ),
    );

    await Promise.all(
      sessionIds.map((sessionId) =>
        sessionService.deleteSession(userId, sessionId),
      ),
    );

    await deleteDoc(getProgramDoc(userId, programId));
  }

  // ─── Coach programs (plan 3g) ────────────────────────────────────────

  /**
   * One `getDoc` on the athlete's copy at
   * `organisations/{orgId}/programs/{programId}`. Returns null when the doc
   * is gone or unreadable (athlete removed from the org) so the caller can
   * fall back to `programLinkage` data. Never lists the org collection.
   */
  static async getOrganisationProgram(
    organisationId: string,
    programId: string,
  ): Promise<OrganisationProgram | null> {
    if (!organisationId || !programId) return null;
    try {
      const snap = await getDoc(
        doc(
          db,
          ORGANISATIONS_COLLECTION,
          organisationId,
          PROGRAMS_SUBCOLLECTION,
          programId,
        ),
      );
      if (!snap.exists()) return null;
      const data: any = snap.data();
      const assignments: ProgramAssignmentLike[] = Array.isArray(
        data?.assignments,
      )
        ? data.assignments.map((a: any) => ({
            type: typeof a?.type === "string" ? a.type : undefined,
            targetId: typeof a?.targetId === "string" ? a.targetId : undefined,
            startDate:
              typeof a?.startDate === "string" ? a.startDate : undefined,
            assignedBy:
              typeof a?.assignedBy === "string" ? a.assignedBy : undefined,
          }))
        : [];
      const durationWeeks = clampDurationWeeks(data?.durationWeeks ?? 1);
      return {
        id: snap.id,
        organisationId,
        name: typeof data?.name === "string" ? data.name : "",
        description:
          typeof data?.description === "string" ? data.description : "",
        startDate:
          typeof data?.startDate === "string" ? data.startDate : undefined,
        durationWeeks,
        weeks: buildProgramWeeks(
          durationWeeks,
          Array.isArray(data?.weeks) ? data.weeks : [],
        ),
        linkedSessions: Array.isArray(data?.linkedSessions)
          ? data.linkedSessions.filter((ref: any) => ref?.sessionId)
          : [],
        assignments,
        createdBy:
          typeof data?.createdBy === "string" ? data.createdBy : undefined,
        masterProgramId:
          typeof data?.masterProgramId === "string"
            ? data.masterProgramId
            : undefined,
        isActive:
          typeof data?.isActive === "boolean" ? data.isActive : undefined,
      };
    } catch (error) {
      console.warn(
        `Could not read organisation program ${organisationId}/${programId}:`,
        error,
      );
      return null;
    }
  }

  /** Drop cached organisation names (tests, sign-out). */
  static clearProgramCaches(): void {
    organisationNameCache.clear();
  }

  /** `organisations/{orgId}.name`, cached per page load; null when unreadable. */
  static getOrganisationName(organisationId: string): Promise<string | null> {
    if (!organisationId) return Promise.resolve(null);
    const cached = organisationNameCache.get(organisationId);
    if (cached) return cached;
    const lookup = getDoc(doc(db, ORGANISATIONS_COLLECTION, organisationId))
      .then((snap) => {
        const name = snap.exists() ? snap.data()?.name : undefined;
        return typeof name === "string" && name.trim() ? name.trim() : null;
      })
      .catch((error) => {
        console.warn(`Could not read organisation ${organisationId}:`, error);
        return null;
      });
    organisationNameCache.set(organisationId, lookup);
    return lookup;
  }

  /**
   * Every program the athlete can see, built from their sessions plus their
   * own `programs/{uid}/programs` docs (plan 3g, audit §3):
   *
   * - sessions are grouped by `programLinkage.programId` (legacy `programID`);
   *   groups with an `organisationID` read the coach copy, the rest the
   *   athlete's own doc — one `getDoc` per distinct program, no scans;
   * - `isActive` is ignored for coach programs (Elite defaults it false);
   * - a missing doc yields a synthetic program from the linkage data so
   *   logged sessions of a removed program still show;
   * - assigner = organisation name + the coach already hydrated on the
   *   sessions (`session.coach`), "You" for self programs.
   *
   * `sessions` should be the athlete's sessions (already coach-hydrated by
   * `sessionService`). Pass `selfPrograms` when the caller already loaded
   * them to avoid a second read.
   */
  static async getAthletePrograms(
    userId: string,
    sessions: (Session & { id: string })[],
    selfPrograms?: (Program & { id: string })[],
    today: Date = new Date(),
  ): Promise<AthleteProgram[]> {
    if (!userId) throw new Error("User ID is required");

    type Group = {
      programId: string;
      organisationId?: string;
      sessions: (Session & { id: string })[];
    };
    const groups = new Map<string, Group>();
    for (const session of sessions) {
      const programId =
        session.programLinkage?.programId || (session as any).programID;
      if (!programId || session.status === "rejected") continue;
      const organisationId = session.organisationID || undefined;
      const key = `${organisationId ?? "self"}:${programId}`;
      const group: Group = groups.get(key) ?? {
        programId,
        organisationId,
        sessions: [],
      };
      group.sessions.push(session);
      groups.set(key, group);
    }

    const ownPrograms =
      selfPrograms ?? (await ProgramService.getAllPrograms(userId));
    const ownById = new Map(ownPrograms.map((p) => [p.id, p]));

    const results: AthleteProgram[] = [];

    // Self programs: every doc the athlete owns, with or without sessions.
    for (const program of ownPrograms) {
      const key = `self:${program.id}`;
      const programSessions = groups.get(key)?.sessions ?? [];
      groups.delete(key);
      const startDate = resolveProgramStartDate(
        program,
        programSessions,
        userId,
        today,
      );
      results.push({
        id: program.id,
        source: "self",
        name: program.name,
        description: program.description,
        startDate,
        durationWeeks: program.durationWeeks,
        weeks: program.weeks,
        isActive: program.isActive,
        fromLinkageOnly: false,
        assigner: { kind: "self" },
        sessions: programSessions,
        progress: computeProgramProgress(
          {
            startDate,
            durationWeeks: program.durationWeeks,
            linkedSessionCount: program.linkedSessions.length,
          },
          programSessions,
          today,
        ),
      });
    }

    // Coach programs (and orphaned self linkages): one doc read per group.
    const remaining = Array.from(groups.values());
    const docs = await Promise.all(
      remaining.map((group) =>
        group.organisationId
          ? ProgramService.getOrganisationProgram(
              group.organisationId,
              group.programId,
            )
          : ownById.has(group.programId)
            ? Promise.resolve(null)
            : ProgramService.getProgram(userId, group.programId).catch(
                () => null,
              ),
      ),
    );

    const orgIds = Array.from(
      new Set(
        remaining
          .map((g) => g.organisationId)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const orgNames = new Map<string, string | null>();
    await Promise.all(
      orgIds.map(async (orgId) => {
        orgNames.set(orgId, await ProgramService.getOrganisationName(orgId));
      }),
    );

    for (let i = 0; i < remaining.length; i++) {
      const group = remaining[i];
      const docData = docs[i];
      const linkage = group.sessions.find((s) => s.programLinkage)
        ?.programLinkage;
      const durationWeeks = clampDurationWeeks(
        docData?.durationWeeks ?? linkage?.durationWeeks ?? 1,
      );
      const startDate = resolveProgramStartDate(
        docData ?? {},
        group.sessions,
        userId,
        today,
      );
      const legacyName = group.sessions
        .map((s) => (s as any).programName)
        .find((n) => typeof n === "string" && n.trim());
      const name =
        (docData?.name && docData.name.trim()) ||
        linkage?.programName ||
        legacyName ||
        "Program";

      let assigner: ProgramAssigner = { kind: "self" };
      if (group.organisationId) {
        const orgDoc = docData as OrganisationProgram | null;
        const assignedBy =
          orgDoc?.assignments.find(
            (a) => a.type === "individual" && a.targetId === userId,
          )?.assignedBy ??
          orgDoc?.assignments[0]?.assignedBy ??
          orgDoc?.createdBy;
        const coachSession =
          group.sessions.find(
            (s) => s.coach && (!assignedBy || s.coachID === assignedBy),
          ) ?? group.sessions.find((s) => s.coach);
        const coachId =
          assignedBy ??
          coachSession?.coachID ??
          group.sessions.find((s) => s.coachID)?.coachID;
        let coachName: string | undefined = coachSession?.coach
          ? [coachSession.coach.name, coachSession.coach.surname]
              .filter(Boolean)
              .join(" ")
          : undefined;
        let coachImageUrl = coachSession?.coach?.imageUrl;
        if (!coachName && coachId) {
          const publicData = await getUserPublicData(coachId).catch(
            () => null,
          );
          if (publicData?.username) coachName = `@${publicData.username}`;
          coachImageUrl = coachImageUrl ?? publicData?.imageUrl;
        }
        assigner = {
          kind: "coach",
          coachId,
          coachName,
          coachImageUrl,
          organisationName: orgNames.get(group.organisationId) ?? undefined,
        };
      }

      results.push({
        id: group.programId,
        source: group.organisationId ? "organisation" : "self",
        organisationId: group.organisationId,
        name,
        description: docData?.description ?? "",
        startDate,
        durationWeeks,
        weeks: docData?.weeks ?? buildProgramWeeks(durationWeeks),
        isActive: group.organisationId ? undefined : docData?.isActive,
        fromLinkageOnly: !docData,
        assigner,
        sessions: group.sessions,
        progress: computeProgramProgress(
          {
            startDate,
            durationWeeks,
            linkedSessionCount: docData?.linkedSessions.length,
          },
          group.sessions,
          today,
        ),
      });
    }

    // Newest start first; ties keep coach programs ahead of self programs.
    return results.sort((a, b) => {
      const byStart = b.startDate.localeCompare(a.startDate);
      if (byStart !== 0) return byStart;
      if (a.source !== b.source) return a.source === "organisation" ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }
}

/**
 * The program whose window contains today (audit risk 5): prefer one that is
 * in progress, then the most recently started. Used by the Dashboard card.
 */
export const pickActiveProgram = (
  programs: AthleteProgram[],
): AthleteProgram | null => {
  const inProgress = programs.filter((p) => p.progress.state === "inProgress");
  if (inProgress.length === 0) return null;
  return [...inProgress].sort((a, b) =>
    b.startDate.localeCompare(a.startDate),
  )[0];
};

export const programService = ProgramService;
